import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getUserAvatarColor } from "../../../utils/kpiColors";

const EVENT_TYPES = [
  { label: "All Events", value: "ALL" },
  { label: "KPI Awards", value: "KPI_AWARD" },
  { label: "Status Changes", value: "STATUS_CHANGE" },
  { label: "Created", value: "CREATE" },
  { label: "Updated", value: "UPDATE" },
];

const KPIActivityLog = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activityLog, loading } = useSelector((state) => state.kpi);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEventType, setSelectedEventType] = useState("ALL");

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: 10,
    };
    if (selectedEventType !== "ALL") {
      params.event_type = selectedEventType;
    }
    dispatch.kpi.getActivityLog(params);
  }, [dispatch, currentPage, selectedEventType]);

  // Extract period groups and pagination metadata safely from redux state or response wrapper
  const periodGroups = Array.isArray(activityLog)
    ? activityLog
    : (Array.isArray(activityLog?.data) ? activityLog.data : []);

  const pagination = activityLog?.pagination || {
    page: currentPage,
    limit: 10,
    total: periodGroups.reduce((acc, g) => acc + (g.activities?.length || 0), 0),
    total_pages: 1,
    has_next: false,
    has_previous: false,
  };

  const formatEventType = (typeStr) => {
    if (!typeStr) return "Activity";
    const raw = String(typeStr).trim();
    return raw
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatLogValue = (val) => {
    if (val === undefined || val === null) return "";
    let objVal = val;
    if (typeof val === 'string' && val.trim().startsWith('{') && val.trim().endsWith('}')) {
      try { objVal = JSON.parse(val); } catch (e) {}
    }
    if (typeof objVal === "object" && objVal !== null) {
      if (objVal.status_name || objVal.task_status) return String(objVal.status_name || objVal.task_status).replace(/"/g, "");
      if (objVal.task_priority || objVal.priority) return String(objVal.task_priority || objVal.priority).replace(/"/g, "");
      if (objVal.name || objVal.title) return String(objVal.name || objVal.title).replace(/"/g, "");
      if (objVal.date || objVal.due_date || objVal.task_end_date) objVal = objVal.date || objVal.due_date || objVal.task_end_date;
      else return JSON.stringify(objVal).replace(/"/g, "");
    }

    const str = String(objVal).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str) || (str.includes("T") && str.includes("Z"))) {
      const d = new Date(str);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
      }
    }
    return str.replace(/"/g, "");
  };

  const parseValueObj = (val) => {
    if (!val) return {};
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object') return parsed;
        } catch (e) {}
      }
    }
    return {};
  };

  const getEventBadge = (eventType, points) => {
    const rawUpper = String(eventType || "").toUpperCase();
    if (rawUpper === "KPI_AWARD" || rawUpper === "AWARD") {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
          +{points !== undefined ? points : 0} pts
        </span>
      );
    }
    if (rawUpper === "KPI_DEDUCTION" || rawUpper === "DEDUCTION") {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          {points !== undefined ? points : 0} pts
        </span>
      );
    }

    const label = formatEventType(eventType);
    return (
      <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
        {label}
      </span>
    );
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="w-full">
      {/* Breadcrumb & Navigation */}
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-4">
        <Link to="/kpi/dashboard" className="hover:text-slate-900 transition-colors">
          Dashboard
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-semibold">Activity log</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900">Activity log</h1>
            {pagination.total > 0 && (
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                {pagination.total} activities
              </span>
            )}
          </div>
          <p className="text-slate-500 text-sm">
            Complete timeline audit log of all KPI tasks, awards, status changes, and system updates
          </p>
        </div>

        <button
          onClick={() => navigate("/kpi/dashboard")}
          className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          Back to Dashboard
        </button>
      </div>

      {/* Event Type Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 border-b border-slate-200">
        {EVENT_TYPES.map((type) => (
          <button
            key={type.value}
            onClick={() => {
              setSelectedEventType(type.value);
              setCurrentPage(1);
            }}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${
              selectedEventType === type.value
                ? "bg-slate-900 text-white shadow-xs"
                : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200/80"
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Timeline Section */}
      <div className="space-y-8">
        {periodGroups.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-12 text-center text-slate-500">
            <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <div className="text-base font-semibold text-slate-900">No activity logs found</div>
            <p className="text-xs text-slate-400 mt-1">There are no records matching your current filter.</p>
          </div>
        ) : (
          periodGroups.map((group, groupIdx) => {
            const activities = group.activities || [];
            if (activities.length === 0) return null;

            return (
              <div key={groupIdx} className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
                {/* Period Section Banner */}
                <div className="px-6 py-3.5 bg-[#f7f6f3] border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600 font-mono">
                    {group.period || "Activity Log"}
                  </span>
                  <span className="text-xs font-medium text-slate-400">
                    {activities.length} {activities.length === 1 ? "event" : "events"}
                  </span>
                </div>

                {/* Activity Items */}
                <div className="divide-y divide-slate-100">
                  {activities.map((act, actIdx) => {
                    const actorName = (act.actor?.full_name || act.actor?.name || "Member").replace(/"/g, "");
                    const nameParts = actorName.trim().split(" ");
                    const initials = nameParts.length >= 2
                      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
                      : actorName.slice(0, 2).toUpperCase();

                    const rawUpper = String(act.event_type || act.action || act.type || "").toUpperCase();
                    const workName = (act.work_name || act.work_id || "").replace(/"/g, "");

                    let desc = act.description || "";

                    const parseValueObj = (val) => {
                      if (!val) return {};
                      if (typeof val === 'object') return val;
                      if (typeof val === 'string') {
                        const trimmed = val.trim();
                        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                          try {
                            const parsed = JSON.parse(trimmed);
                            if (parsed && typeof parsed === 'object') return parsed;
                          } catch (e) {}
                        }
                      }
                      return {};
                    };

                    const prevValObj = parseValueObj(act.previous_value);
                    const newValObj = parseValueObj(act.new_value);

                    const getGroupVal = (val, valDetails) => {
                      if (valDetails && valDetails.group_id) {
                        const detailVal = valDetails.group_id;
                        if (typeof detailVal === "object" && detailVal !== null) {
                          if (detailVal.group_name || detailVal.name || detailVal.title) {
                            return String(detailVal.group_name || detailVal.name || detailVal.title).trim();
                          }
                        }
                      }
                      if (val === undefined || val === null) return null;
                      if (typeof val === "object" && val !== null) {
                        const extracted = val.group_name || val.name || val.title || val.group_id || val.group;
                        if (extracted === undefined || extracted === null) return null;
                        return getGroupVal(extracted);
                      }
                      const str = String(val).trim();
                      if (str === "" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
                      return str.replace(/"/g, "");
                    };

                    const getAssigneeName = (valObj, valDetails) => {
                      const details = (valObj && valObj.assigned_to_details) || (valDetails && valDetails.assigned_to_details) || (valDetails && (valDetails.assigned_to || valDetails.assignee));
                      if (details && Array.isArray(details) && details.length > 0) {
                        return details
                          .map((u) => {
                            if (typeof u === "object" && u !== null) {
                              return u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email || "";
                            }
                            return String(u);
                          })
                          .filter(Boolean)
                          .join(", ")
                          .replace(/"/g, "");
                      }
                      const target = (valObj && (valObj.assigned_to || valObj.assignee)) || (valDetails && (valDetails.assigned_to || valDetails.assignee)) || valObj;
                      if (!target) return "";
                      if (Array.isArray(target)) {
                        return target
                          .map((u) => {
                            if (typeof u === "object" && u !== null) {
                              return u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name || u.email || "";
                            }
                            return String(u);
                          })
                          .filter(Boolean)
                          .join(", ")
                          .replace(/"/g, "");
                      }
                      if (typeof target === "object" && target !== null) {
                        return (target.full_name || `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.name || target.email || "").replace(/"/g, "");
                      }
                      return String(target).replace(/"/g, "");
                    };

                    const wId = (act.work_id || act.task_details?.work_id || newValObj.work_id || prevValObj.work_id || "").replace(/"/g, "").trim();
                    const wName = (act.work_name || act.task_details?.task_name || act.target || newValObj.work_name || newValObj.task_name || prevValObj.work_name || prevValObj.task_name || "").replace(/"/g, "").trim();

                    const workTitle = (wId && wName && wId.toLowerCase() !== wName.toLowerCase())
                      ? `${wId} (${wName})`
                      : (wId || wName || "work");

                    const isTaskDeleted =
                      rawUpper === "TASK_DELETED" ||
                      rawUpper === "TASK_DELETE" ||
                      rawUpper === "WORK_DELETED" ||
                      rawUpper === "WORK_DELETE" ||
                      rawUpper === "DELETE" ||
                      rawUpper.includes("DELETE") ||
                      act.is_deleted === true ||
                      newValObj.is_deleted === true ||
                      newValObj.status_name === "DELETE" ||
                      String(act.action || "").toUpperCase().includes("DELETE");

                    const taskId = isTaskDeleted ? null : (act.task_id || act.work_id);

                    if (isTaskDeleted) {
                      desc = `${actorName} deleted task ${workTitle}`;
                    } else if (rawUpper === "DESCRIPTION_CHANGE" || rawUpper === "TASK_DESCRIPTION_CHANGE" || "task_description" in prevValObj || "task_description" in newValObj) {
                      desc = `${actorName} changed description of ${workTitle}`;
                    } else if (rawUpper === "ASSIGNMENT_CHANGE" || rawUpper === "ASSIGNEE_CHANGE" || "assigned_to" in prevValObj || "assigned_to" in newValObj || "assigned_to_details" in prevValObj || "assigned_to_details" in newValObj) {
                      const prevAssignee = getAssigneeName(prevValObj, act.previous_value_details);
                      const newAssignee = getAssigneeName(newValObj, act.new_value_details);

                      if (prevAssignee && newAssignee && prevAssignee !== newAssignee) {
                        desc = `${actorName} changed assignee of ${workTitle} from ${prevAssignee} to ${newAssignee}`;
                      } else if (newAssignee) {
                        desc = `${actorName} assigned ${workTitle} to ${newAssignee}`;
                      } else if (prevAssignee) {
                        desc = `${actorName} unassigned ${workTitle} from ${prevAssignee}`;
                      } else {
                        desc = `${actorName} updated assignee of ${workTitle}`;
                      }
                    } else if (
                      rawUpper === "DUE_DATE_CHANGE" ||
                      rawUpper === "DUE_DATE_UPDATED" ||
                      rawUpper === "TASK_DUE_DATE_CHANGE" ||
                      rawUpper === "DUE_DATE" ||
                      rawUpper.includes("DUE_DATE") ||
                      rawUpper.includes("END_DATE") ||
                      "task_end_date" in prevValObj ||
                      "task_end_date" in newValObj ||
                      "due_date" in prevValObj ||
                      "due_date" in newValObj
                    ) {
                      const prevDate = formatLogValue(prevValObj.task_end_date || prevValObj.due_date || (typeof act.previous_value !== 'object' && !String(act.previous_value).startsWith('{') ? act.previous_value : ''));
                      const newDate = formatLogValue(newValObj.task_end_date || newValObj.due_date || (typeof act.new_value !== 'object' && !String(act.new_value).startsWith('{') ? act.new_value : ''));

                      if (prevDate && newDate && prevDate !== newDate) {
                        desc = `${actorName} changed due date of ${workTitle} from ${prevDate} to ${newDate}`;
                      } else if (newDate) {
                        desc = `${actorName} set due date of ${workTitle} to ${newDate}`;
                      } else {
                        desc = `${actorName} updated due date of ${workTitle}`;
                      }
                    } else if (
                      rawUpper.includes("GROUP") ||
                      "group_id" in prevValObj ||
                      "group_id" in newValObj ||
                      "group_name" in prevValObj ||
                      "group_name" in newValObj
                    ) {
                      const prevGroup = getGroupVal(prevValObj.group_id ?? prevValObj.group_name ?? prevValObj.group ?? (typeof act.previous_value !== 'object' && !String(act.previous_value).startsWith('{') ? act.previous_value : null), act.previous_value_details);
                      const newGroup = getGroupVal(newValObj.group_id ?? newValObj.group_name ?? newValObj.group ?? (typeof act.new_value !== 'object' && !String(act.new_value).startsWith('{') ? act.new_value : null), act.new_value_details);

                      if (!prevGroup && newGroup) {
                        desc = `${actorName} moved ${workTitle} to group ${newGroup}`;
                      } else if (prevGroup && newGroup && prevGroup !== newGroup) {
                        desc = `${actorName} moved ${workTitle} from group ${prevGroup} to ${newGroup}`;
                      } else if (prevGroup && !newGroup) {
                        desc = `${actorName} removed ${workTitle} from group ${prevGroup}`;
                      } else if (newGroup) {
                        desc = `${actorName} moved ${workTitle} to group ${newGroup}`;
                      }
                    } else if (rawUpper.includes("COMMENT") || (desc && desc.toLowerCase().includes("comment")) || newValObj.comment_text || newValObj.comment) {
                      const commentContent = (act.comment || act.message || act.text || newValObj.comment_text || newValObj.comment || "").replace(/"/g, "").trim();
                      desc = `${actorName} added a comment on ${workTitle}${commentContent ? `: "${commentContent}"` : ""}`;
                    } else if (rawUpper === "CREATE" || rawUpper === "WORK_CREATE" || rawUpper === "TASK_CREATE") {
                      desc = `${actorName} created task ${workTitle}`;
                    } else if (rawUpper === "STATUS_CHANGE" || rawUpper === "STATUS_CHANGED" || "status_name" in prevValObj || "status_name" in newValObj) {
                      const prevStatus = (act.old_value?.status_name || act.previous_value?.status_name || prevValObj.status_name || "").replace(/"/g, "");
                      const newStatus = (act.new_value?.status_name || act.status_name || newValObj.status_name || "").replace(/"/g, "");
                      if (prevStatus && newStatus && prevStatus !== newStatus) {
                        desc = `${actorName} changed status of ${workTitle}: ${prevStatus} → ${newStatus}`;
                      } else if (newStatus) {
                        desc = `${actorName} updated status of ${workTitle} to ${newStatus}`;
                      } else {
                        desc = `${actorName} updated status of ${workTitle}`;
                      }
                    } else if (rawUpper === "PRIORITY_CHANGE" || rawUpper === "PRIORITY_CHANGED" || "task_priority" in prevValObj || "task_priority" in newValObj) {
                      const prevPrio = (prevValObj.task_priority || prevValObj.priority || "").replace(/"/g, "");
                      const newPrio = (act.new_value?.task_priority || act.new_value?.priority || newValObj.task_priority || newValObj.priority || "").replace(/"/g, "");
                      if (prevPrio && newPrio && prevPrio !== newPrio) {
                        desc = `${actorName} changed priority of ${workTitle} from ${prevPrio} to ${newPrio}`;
                      } else if (newPrio) {
                        desc = `${actorName} changed priority of ${workTitle} to ${newPrio}`;
                      } else {
                        desc = `${actorName} updated priority of ${workTitle}`;
                      }
                    } else if (rawUpper === "KPI_AWARD" || rawUpper === "AWARD") {
                      const pts = act.points !== undefined ? act.points : (newValObj.assigned_kpi_points || act.pts || 0);
                      desc = `${actorName} awarded ${pts} KPI points for ${workTitle}`;
                    } else if (rawUpper === "KPI_DEDUCTION" || rawUpper === "DEDUCTION") {
                      const pts = act.points !== undefined ? act.points : (newValObj.assigned_kpi_points || act.pts || 0);
                      desc = `${actorName} deducted ${pts} KPI points for ${workTitle}`;
                    } else if (desc) {
                      desc = desc.replace(/performed Comment Added/gi, "added a comment");
                      desc = desc.replace(/performed\s+/gi, "updated ");
                      desc = desc.replace(/\s*(?:→|->|=>)\s*/g, " - ");
                      desc = desc.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/g, (match) => {
                        const d = new Date(match);
                        return !isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : match;
                      });
                    } else {
                      desc = `${actorName} updated ${workTitle}`;
                    }
                    desc = desc.replace(/performed Comment Added/gi, "added a comment");
                    desc = desc.replace(/\s*(?:→|->|=>)\s*/g, " - ");
                    desc = desc.replace(/"/g, "");

                    const avatarColor = getUserAvatarColor(act.actor?._id || actorName, actIdx);

                    return (
                      <div
                        key={act.activity_id || act._id || actIdx}
                        className="p-5 hover:bg-slate-50/70 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4"
                      >
                        <div className="flex items-start gap-4 min-w-0">
                          <div className={`w-9 h-9 rounded-full ${avatarColor} text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 shadow-2xs`}>
                            {initials}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-900 leading-snug">
                              {desc}
                            </div>

                            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                              {/* Task Link */}
                              {taskId && (
                                <Link
                                  to={`/kpi/works/${taskId}`}
                                  className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50/70 px-2.5 py-0.5 rounded transition-colors"
                                >
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                                  {act.work_id ? `${act.work_id} · ` : ""}{workName}
                                </Link>
                              )}

                              {/* Actor email if available */}
                              {act.actor?.email && (
                                <span className="text-xs text-slate-400">{act.actor.email}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Column: Timestamp */}
                        <div className="flex items-center gap-4 shrink-0 md:self-center pl-13 md:pl-0">
                          <div className="text-xs font-medium text-slate-400 font-mono text-right">
                            {formatTime(act.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Footer */}
      {pagination.total_pages > 1 && (
        <div className="mt-8 bg-white rounded-2xl border border-slate-200/90 shadow-xs px-6 py-4 flex items-center justify-between">
          <div className="text-xs font-medium text-slate-500">
            Page <span className="font-bold text-slate-900">{pagination.page || currentPage}</span> of{" "}
            <span className="font-bold text-slate-900">{pagination.total_pages}</span> ({pagination.total} total)
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={!pagination.has_previous && (pagination.page || currentPage) <= 1}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={!pagination.has_next && (pagination.page || currentPage) >= pagination.total_pages}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIActivityLog;
